/**
 * Marmitek Cookie Consent Banner
 *
 * Vanilla Web Component that replaces Cookiebot. Wires up Shopify's
 * Customer Privacy API and Google Consent Mode v2.
 */

const CONSENT_CATEGORIES = ['necessary', 'analytics', 'marketing', 'preferences'];
const STORAGE_KEY = 'marmitek_consent_v1';

const spring = (from, to, onUpdate, opts = {}) => {
  const { stiffness = 280, damping = 26, mass = 1, precision = 0.01 } = opts;
  let position = from;
  let velocity = 0;
  let last = performance.now();
  let raf;
  const tick = (now) => {
    const dt = Math.min((now - last) / 1000, 1 / 30);
    last = now;
    const force = -stiffness * (position - to);
    const dampingForce = -damping * velocity;
    const acceleration = (force + dampingForce) / mass;
    velocity += acceleration * dt;
    position += velocity * dt;
    onUpdate(position);
    if (Math.abs(velocity) < precision && Math.abs(position - to) < precision) {
      onUpdate(to);
      return;
    }
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(raf);
};

const STYLES = `
  :host {
    --cb-bg: #FFFFFF;
    --cb-bg-elevated: #F8F8F8;
    --cb-text: #0A0A0A;
    --cb-text-muted: #6B6B6B;
    --cb-text-faint: #A3A3A3;
    --cb-border: rgba(10, 10, 10, 0.08);
    --cb-border-strong: rgba(10, 10, 10, 0.16);
    --cb-accent: #3C0050;
    --cb-accent-soft: rgba(60, 0, 80, 0.06);
    --cb-accent-border: rgba(60, 0, 80, 0.14);
    --cb-success: #18a957;
    --cb-radius: 18px;
    --cb-radius-btn: 10px;
    --cb-shadow:
      0 24px 60px -18px rgba(10, 10, 10, 0.18),
      0 6px 14px -6px rgba(10, 10, 10, 0.08),
      0 0 0 1px rgba(10, 10, 10, 0.04);
    --cb-font: 'Poppins', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 999999;
    font-family: var(--cb-font);
    color: var(--cb-text);
  }
  :host([hidden]) { display: none; }

  .banner, .banner * {
    font-family: var(--cb-font);
    font-feature-settings: 'kern' 1, 'liga' 1, 'ss01' 1;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    box-sizing: border-box;
  }

  .banner {
    position: absolute;
    bottom: 24px;
    left: 24px;
    transform: translateY(120%);
    width: min(calc(100vw - 32px), 420px);
    max-height: calc(100vh - 48px);
    background: var(--cb-bg);
    border: 1px solid var(--cb-border);
    border-radius: var(--cb-radius);
    box-shadow: var(--cb-shadow);
    overflow: hidden;
    pointer-events: auto;
    will-change: transform, opacity;
    opacity: 0;
    display: flex;
    flex-direction: column;
  }

  .banner-inner {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scrollbar-color: var(--cb-border-strong) transparent;
  }
  .banner-inner::-webkit-scrollbar { width: 6px; }
  .banner-inner::-webkit-scrollbar-thumb { background: var(--cb-border-strong); border-radius: 3px; }

  .row {
    padding: 18px 22px;
    opacity: 0;
    transform: translateY(8px);
    will-change: transform, opacity;
  }
  .row + .row { padding-top: 0; }

  .header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-bottom: 8px;
  }
  .icon-wrap {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: var(--cb-accent-soft);
    border: 1px solid var(--cb-accent-border);
  }
  .icon-wrap svg {
    width: 18px;
    height: 18px;
    color: var(--cb-accent);
    stroke-width: 2;
  }
  .header-title {
    font-size: 14.5px;
    font-weight: 500;
    letter-spacing: -0.01em;
    color: var(--cb-text);
  }

  .description {
    font-size: 13.5px;
    line-height: 1.55;
    color: var(--cb-text-muted);
    letter-spacing: -0.005em;
  }
  .description p { margin: 0; }
  .privacy-inline {
    color: var(--cb-text);
    text-decoration: none;
    transition: opacity 160ms ease;
    white-space: nowrap;
  }
  .privacy-inline:hover { opacity: 0.65; }
  .privacy-inline svg {
    width: 12px;
    height: 12px;
    margin-left: 4px;
    vertical-align: -1px;
    transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .privacy-inline:hover svg { transform: translateX(3px); }

  .text-link svg {
    width: 14px;
    height: 14px;
    margin-right: 6px;
    vertical-align: -2px;
    transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .text-link:hover svg { transform: translateX(-3px); }

  .customize-panel {
    overflow: hidden;
    height: 0;
    will-change: height;
    border-top: 1px solid var(--cb-border);
    margin-top: 16px;
  }
  .customize-panel-inner { padding: 6px 22px 4px; }
  .category-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 0;
    opacity: 0;
    transform: translateY(6px);
    will-change: transform, opacity;
    border-bottom: 1px solid var(--cb-border);
  }
  .category-row:last-child { border-bottom: none; }
  .category-info { flex: 1; min-width: 0; }
  .category-name {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 500;
    color: var(--cb-text);
    letter-spacing: -0.005em;
  }
  .required-badge {
    display: inline-flex;
    align-items: center;
    height: 18px;
    padding: 0 7px;
    border-radius: 4px;
    background: var(--cb-bg-elevated);
    font-size: 11px;
    font-weight: 400;
    color: var(--cb-text-muted);
  }
  .category-desc {
    margin: 4px 0 0;
    font-size: 12px;
    line-height: 1.5;
    color: var(--cb-text-muted);
  }

  .switch {
    --w: 38px;
    --h: 22px;
    position: relative;
    display: inline-block;
    width: var(--w);
    height: var(--h);
    flex-shrink: 0;
    margin-top: 1px;
    border: none;
    background: #E5E5E5;
    border-radius: var(--h);
    cursor: pointer;
    padding: 0;
    transition: background 220ms ease;
  }
  .switch::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: calc(var(--h) - 4px);
    height: calc(var(--h) - 4px);
    background: #FFFFFF;
    border-radius: 50%;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.10), 0 1px 3px rgba(0, 0, 0, 0.06);
    transition: transform 280ms cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .switch[aria-checked="true"] {
    background: var(--cb-accent);
  }
  .switch[aria-checked="true"]::after {
    transform: translateX(calc(var(--w) - var(--h)));
  }
  .switch:focus-visible {
    outline: 2px solid var(--cb-accent);
    outline-offset: 2px;
  }
  .switch:disabled {
    cursor: not-allowed;
    background: var(--cb-accent);
    opacity: 0.55;
  }

  .actions-block {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 18px 22px 20px;
  }
  .actions-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
  .btn {
    height: 44px;
    padding: 0 16px;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: -0.005em;
    border-radius: var(--cb-radius-btn);
    border: 1px solid transparent;
    cursor: pointer;
    transition:
      transform 100ms cubic-bezier(0.22, 1, 0.36, 1),
      background 180ms ease,
      color 180ms ease,
      border-color 180ms ease;
    will-change: transform;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .btn:active { transform: scale(0.98); }
  .btn:focus-visible {
    outline: 2px solid var(--cb-accent);
    outline-offset: 2px;
  }
  .btn--primary {
    background: var(--cb-accent);
    color: #FFFFFF;
  }
  .btn--primary:hover { background: #2A0038; }
  .btn--secondary {
    background: #FFFFFF;
    color: var(--cb-text);
    border-color: var(--cb-border-strong);
  }
  .btn--secondary:hover {
    background: var(--cb-bg-elevated);
    border-color: var(--cb-text);
  }
  .btn--full { width: 100%; }

  .text-link-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    margin-top: 10px;
    flex-wrap: wrap;
  }
  .text-link-row::before, .text-link-row::after { content: ''; flex: 0; }
  .text-link {
    background: none;
    border: none;
    padding: 6px 4px;
    font-size: 12.5px;
    font-weight: 500;
    color: var(--cb-text-muted);
    cursor: pointer;
    transition: color 160ms ease;
    font-family: inherit;
    text-decoration: none;
  }
  .text-link--alt { color: var(--cb-text-muted); }
  .text-link:hover { color: var(--cb-text); }
  .text-link:focus-visible {
    outline: 2px solid var(--cb-accent);
    outline-offset: 2px;
    border-radius: 4px;
  }

  .actions-customize, .actions-details { display: none; }
  :host([data-view="preferences"]) .actions-default { display: none; }
  :host([data-view="preferences"]) .actions-customize { display: flex; flex-direction: column; gap: 8px; }
  :host([data-view="details"]) .actions-default { display: none; }
  :host([data-view="details"]) .actions-details { display: flex; flex-direction: column; gap: 8px; }
  :host([data-view="details"]) .customize-panel { display: none; }
  :host([data-view="details"]) .header-desc-row { display: none; }

  .details-panel {
    overflow: hidden;
    height: 0;
    will-change: height;
  }
  :host([data-view="details"]) .details-panel { height: auto; }
  .details-header { padding: 22px 22px 8px; }
  .details-header h3 {
    margin: 0 0 6px;
    font-size: 14.5px;
    font-weight: 500;
    letter-spacing: -0.01em;
    color: var(--cb-text);
  }
  .details-intro {
    font-size: 13px;
    line-height: 1.55;
    color: var(--cb-text-muted);
    margin: 0;
  }
  .details-list { padding: 8px 16px 12px; }
  .detail-cat {
    border-radius: 10px;
    margin-bottom: 6px;
    background: var(--cb-bg-elevated);
    overflow: hidden;
    transition: background 160ms ease;
  }
  .detail-cat:hover { background: rgba(10,10,10,0.04); }
  .detail-cat[open] { background: var(--cb-bg-elevated); }
  .detail-cat summary {
    list-style: none;
    cursor: pointer;
    padding: 11px 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 13px;
    font-weight: 500;
    color: var(--cb-text);
  }
  .detail-cat summary::-webkit-details-marker { display: none; }
  .detail-cat .cat-meta {
    font-size: 11.5px;
    font-weight: 400;
    color: var(--cb-text-muted);
    margin-left: 8px;
  }
  .detail-cat .chev {
    width: 16px;
    height: 16px;
    color: var(--cb-text-muted);
    transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
    flex-shrink: 0;
  }
  .detail-cat[open] .chev { transform: rotate(180deg); }
  .detail-cat ul {
    list-style: none;
    margin: 0;
    padding: 0 14px 10px;
  }
  .detail-cat li {
    padding: 9px 0;
    border-top: 1px solid var(--cb-border);
    font-size: 12px;
    line-height: 1.5;
  }
  .detail-cat li:first-child { border-top: 1px solid var(--cb-border); }
  .detail-cookie-name {
    font-family: ui-monospace, 'SF Mono', Consolas, monospace;
    font-size: 11.5px;
    color: var(--cb-text);
    word-break: break-all;
    margin-bottom: 2px;
  }
  .detail-cookie-meta {
    color: var(--cb-text-muted);
    font-size: 11px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .detail-cookie-meta span:not(:last-child)::after {
    content: '·';
    margin-left: 8px;
    color: var(--cb-text-faint);
  }
  .detail-cookie-purpose {
    color: var(--cb-text-muted);
    margin-top: 3px;
    font-size: 11.5px;
  }

  .confirmed {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 28px 22px;
    font-size: 13.5px;
    color: var(--cb-text);
    font-weight: 500;
  }
  .confirmed-icon {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: rgba(24, 169, 87, 0.12);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .confirmed-icon svg {
    width: 12px;
    height: 12px;
    color: var(--cb-success);
    stroke-width: 2.5;
  }

  @media (prefers-reduced-motion: reduce) {
    .banner, .row, .switch::after, .privacy-link::after { transition: none !important; }
  }

  @media (max-width: 520px) {
    .banner {
      width: calc(100vw - 16px);
      bottom: 8px;
      left: 8px;
      right: 8px;
    }
    .row { padding: 16px 18px; }
    .actions-block { padding: 14px 18px 18px; }
    .actions-row { grid-template-columns: 1fr; gap: 6px; }
    .customize-panel-inner { padding-left: 18px; padding-right: 18px; }
  }
`;

// Lucide icons (https://lucide.dev) — MIT licensed
const SVG_ATTRS = 'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"';
const COOKIE_ICON = `
  <svg ${SVG_ATTRS}>
    <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5"/>
    <path d="M8.5 8.5v.01"/>
    <path d="M16 15.5v.01"/>
    <path d="M12 12v.01"/>
    <path d="M11 17v.01"/>
    <path d="M7 14v.01"/>
  </svg>
`;
const CHECK_ICON = `
  <svg ${SVG_ATTRS}>
    <path d="M20 6 9 17l-5-5"/>
  </svg>
`;
const ARROW_RIGHT = `
  <svg ${SVG_ATTRS}>
    <path d="M18 8L22 12L18 16"/>
    <path d="M2 12H22"/>
  </svg>
`;
const ARROW_LEFT = `
  <svg ${SVG_ATTRS}>
    <path d="M6 8L2 12L6 16"/>
    <path d="M2 12H22"/>
  </svg>
`;
const CHEVRON_DOWN = `
  <svg ${SVG_ATTRS}>
    <path d="m6 9 6 6 6-6"/>
  </svg>
`;

// Cookie inventory shown in the details view. Strings come from l10n.declaration.*
const COOKIES_DATA = {
  necessary: [
    { name: 'cart', provider: 'Shopify', purpose: 'cookie_cart', expiry: '2w' },
    { name: '_shopify_y', provider: 'Shopify', purpose: 'cookie_shopify_y', expiry: '1y' },
    { name: '_shopify_s', provider: 'Shopify', purpose: 'cookie_shopify_s', expiry: '30min' },
    { name: 'secure_customer_sig', provider: 'Shopify', purpose: 'cookie_secure_sig', expiry: '1y' },
    { name: 'cart_currency', provider: 'Shopify', purpose: 'cookie_cart_currency', expiry: 'session' },
    { name: 'marmitek_consent_v1', provider: 'Marmitek', purpose: 'cookie_marmitek_consent', expiry: '1y' }
  ],
  analytics: [
    { name: '_ga', provider: 'Google Analytics', purpose: 'cookie_ga', expiry: '2y' },
    { name: '_ga_*', provider: 'Google Analytics', purpose: 'cookie_ga_container', expiry: '2y' },
    { name: '_gid', provider: 'Google Analytics', purpose: 'cookie_gid', expiry: '24h' }
  ],
  marketing: [
    { name: '_gcl_au', provider: 'Google Ads', purpose: 'cookie_gcl_au', expiry: '90d' },
    { name: 'tdclid_sn', provider: 'Tradedoubler', purpose: 'cookie_tdclid', expiry: '1y' },
    { name: '_fbp', provider: 'Meta', purpose: 'cookie_fbp', expiry: '90d' },
    { name: 'IDE / NID', provider: 'Google DoubleClick', purpose: 'cookie_ide', expiry: '13mo' }
  ],
  preferences: [
    { name: 'localization', provider: 'Shopify', purpose: 'cookie_localization', expiry: '1y' },
    { name: '_orig_referrer', provider: 'Shopify', purpose: 'cookie_orig_referrer', expiry: 'session' }
  ]
};

const TEMPLATE = (l10n) => `
  <div class="banner" role="dialog" aria-modal="false" aria-labelledby="cb-title" part="banner">
    <div class="banner-inner">
      <div class="row header-desc-row" data-row="header-desc">
        <div class="header">
          <span class="icon-wrap">${COOKIE_ICON}</span>
          <span class="header-title" id="cb-title">${l10n.title || ''}</span>
        </div>
        <div class="description">
          <p>${l10n.description || ''}${l10n.privacy_url ? ` <a class="privacy-inline" href="${l10n.privacy_url}">${l10n.privacy_link || ''}${ARROW_RIGHT}</a>` : ''}</p>
        </div>
      </div>
      <div class="customize-panel" data-panel>
        <div class="customize-panel-inner" data-panel-inner></div>
      </div>
      <div class="details-panel" data-details>
        <div class="details-header">
          <h3>${(l10n.declaration && l10n.declaration.title) || 'Cookie details'}</h3>
          <p class="details-intro">${(l10n.declaration && l10n.declaration.intro) || ''}</p>
        </div>
        <div class="details-list" data-details-list></div>
      </div>
      <div class="row actions-block" data-row="actions">
        <div class="actions-default">
          <div class="actions-row">
            <button class="btn btn--secondary" data-action="reject-all">${l10n.reject_all || ''}</button>
            <button class="btn btn--primary" data-action="accept-all">${l10n.accept_all || ''}</button>
          </div>
          <div class="text-link-row">
            <button class="text-link" data-action="customize">${l10n.customize || ''}</button>
          </div>
        </div>
        <div class="actions-customize">
          <button class="btn btn--primary btn--full" data-action="save">${l10n.save_preferences || ''}</button>
          <div class="text-link-row">
            <button class="text-link" data-action="customize-close">${ARROW_LEFT}${l10n.back || ''}</button>
            <button class="text-link text-link--alt" data-action="show-details">${l10n.details_link || ''}${ARROW_RIGHT}</button>
          </div>
        </div>
        <div class="actions-details">
          <button class="btn btn--primary btn--full" data-action="save">${l10n.save_preferences || ''}</button>
          <button class="text-link" data-action="back-to-preferences">${ARROW_LEFT}${l10n.back || ''}</button>
        </div>
      </div>
    </div>
  </div>
`;

const CATEGORY_ROW = (cat, l10n) => `
  <div class="category-row" data-category-row="${cat.id}">
    <div class="category-info">
      <div class="category-name">
        <span>${l10n[`cat_${cat.id}_name`] || ''}</span>
        ${cat.required ? `<span class="required-badge">${l10n.required || ''}</span>` : ''}
      </div>
      <p class="category-desc">${l10n[`cat_${cat.id}_desc`] || ''}</p>
    </div>
    <button
      class="switch"
      role="switch"
      aria-checked="${cat.required || cat.checked ? 'true' : 'false'}"
      aria-label="${l10n[`cat_${cat.id}_name`] || ''}"
      data-toggle="${cat.id}"
      ${cat.required ? 'disabled' : ''}
    ></button>
  </div>
`;

class CookieBanner extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = {
      preferences: { necessary: true, analytics: false, marketing: false, preferences: false },
      view: 'default', // 'default' | 'preferences' | 'details'
      consented: null
    };
    this.l10n = {};
    this.handleHashChange = this.handleHashChange.bind(this);
  }

  connectedCallback() {
    try {
      this.l10n = JSON.parse(this.getAttribute('data-l10n') || '{}');
    } catch (e) {
      this.l10n = {};
    }
    this.render();
    this.bindEvents();
    this.show();
    const initialView = this.getAttribute('data-initial-view');
    if (initialView && initialView !== 'default') {
      // Defer slightly so the show() animation can start first
      setTimeout(() => this.setView(initialView), 80);
    } else {
      this.applyHash();
    }
    window.addEventListener('hashchange', this.handleHashChange);
  }

  disconnectedCallback() {
    window.removeEventListener('hashchange', this.handleHashChange);
  }

  handleHashChange() {
    this.applyHash();
  }

  applyHash() {
    const hash = (window.location.hash || '').toLowerCase();
    if (hash === '#cookies' || hash === '#cookie-preferences') {
      this.setView('preferences', { silent: true });
    } else if (hash === '#cookie-details') {
      this.setView('details', { silent: true });
    }
  }

  updateHash(view) {
    const map = { 'default': '', 'preferences': '#cookie-preferences', 'details': '#cookie-details' };
    const target = map[view] ?? '';
    if (target && window.location.hash !== target) {
      history.replaceState({}, '', `${window.location.pathname}${window.location.search}${target}`);
    } else if (!target && window.location.hash) {
      history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
    }
  }

  render() {
    const style = document.createElement('style');
    style.textContent = STYLES;
    this.shadowRoot.appendChild(style);
    const wrapper = document.createElement('div');
    wrapper.innerHTML = TEMPLATE(this.l10n);
    while (wrapper.firstChild) this.shadowRoot.appendChild(wrapper.firstChild);
  }

  bindEvents() {
    this.shadowRoot.addEventListener('click', (e) => {
      const action = e.target.closest('[data-action]')?.dataset.action;
      const toggle = e.target.closest('[data-toggle]')?.dataset.toggle;
      if (action === 'accept-all') this.acceptAll();
      else if (action === 'reject-all') this.rejectAll();
      else if (action === 'customize') this.setView('preferences');
      else if (action === 'customize-close') this.setView('default');
      else if (action === 'show-details') this.setView('details');
      else if (action === 'back-to-preferences') this.setView('preferences');
      else if (action === 'save') this.savePreferences();
      else if (toggle) this.toggleCategory(toggle);
    });

    this.shadowRoot.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (this.state.view === 'details') this.setView('preferences');
        else if (this.state.view === 'preferences') this.setView('default');
      }
    });
  }

  show() {
    const banner = this.shadowRoot.querySelector('.banner');
    const rows = this.shadowRoot.querySelectorAll('[data-row]');

    spring(120, 0, (v) => {
      banner.style.transform = `translateY(${v}%)`;
      banner.style.opacity = String(Math.max(0, Math.min(1, 1 - v / 60)));
    }, { stiffness: 320, damping: 30 });

    rows.forEach((row, i) => {
      setTimeout(() => {
        row.style.transition = 'opacity 280ms ease, transform 280ms cubic-bezier(0.22, 1, 0.36, 1)';
        row.style.opacity = '1';
        row.style.transform = 'translateY(0)';
      }, 60 + i * 60);
    });
  }

  toggleCategory(id) {
    if (id === 'necessary') return;
    this.state.preferences[id] = !this.state.preferences[id];
    const sw = this.shadowRoot.querySelector(`[data-toggle="${id}"]`);
    if (sw) sw.setAttribute('aria-checked', String(this.state.preferences[id]));
  }

  setView(view, opts = {}) {
    if (this.state.view === view) return;
    const prev = this.state.view;
    this.state.view = view;
    this.setAttribute('data-view', view);

    const panel = this.shadowRoot.querySelector('[data-panel]');
    const detailsPanel = this.shadowRoot.querySelector('[data-details]');

    if (view === 'preferences') {
      this.renderPreferencesPanel();
      this.animateExpand(panel);
    } else if (view === 'details') {
      this.renderDetailsPanel();
    } else if (view === 'default') {
      if (prev === 'preferences') this.animateCollapse(panel);
    }

    if (!opts.silent) this.updateHash(view);
  }

  renderPreferencesPanel() {
    const inner = this.shadowRoot.querySelector('[data-panel-inner]');
    const cats = CONSENT_CATEGORIES.map(id => ({
      id,
      required: id === 'necessary',
      checked: this.state.preferences[id]
    }));
    inner.innerHTML = cats.map(c => CATEGORY_ROW(c, this.l10n)).join('');
    const rows = inner.querySelectorAll('[data-category-row]');
    rows.forEach((row, i) => {
      setTimeout(() => {
        row.style.transition = 'opacity 260ms ease, transform 260ms cubic-bezier(0.22, 1, 0.36, 1)';
        row.style.opacity = '1';
        row.style.transform = 'translateY(0)';
      }, 100 + i * 50);
    });
  }

  renderDetailsPanel() {
    const list = this.shadowRoot.querySelector('[data-details-list]');
    const decl = (this.l10n.declaration || {});
    const expiryLabel = (raw) => {
      // raw is e.g. '2w', '1y', '30min', 'session'
      const map = {
        'session': decl.session || 'Session',
        'min': decl.minutes || 'min',
        'h': decl.hours || 'h',
        'd': decl.days || 'd',
        'w': decl.weeks || 'w',
        'mo': decl.months || 'mo',
        'y': decl.year || decl.years || 'y'
      };
      if (raw === 'session') return map.session;
      const m = raw.match(/^(\d+)(min|mo|h|d|w|y)$/);
      if (!m) return raw;
      return `${m[1]} ${map[m[2]] || m[2]}`;
    };

    const chev = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="chev" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>`;
    const renderCategory = (catId) => {
      const cookies = COOKIES_DATA[catId] || [];
      const catName = this.l10n[`cat_${catId}_name`] || catId;
      const cookiesLabel = decl.cookies_label || 'cookies';
      const items = cookies.map(c => `
        <li>
          <div class="detail-cookie-name">${c.name}</div>
          <div class="detail-cookie-meta">
            <span>${c.provider}</span>
            <span>${expiryLabel(c.expiry)}</span>
          </div>
          <div class="detail-cookie-purpose">${decl[c.purpose] || ''}</div>
        </li>
      `).join('');
      return `
        <details class="detail-cat">
          <summary>
            <span>${catName}<span class="cat-meta"> · ${cookies.length} ${cookiesLabel}</span></span>
            ${chev}
          </summary>
          <ul>${items}</ul>
        </details>
      `;
    };
    list.innerHTML = CONSENT_CATEGORIES.map(renderCategory).join('');
  }

  animateExpand(panel) {
    panel.style.height = 'auto';
    const targetHeight = panel.scrollHeight;
    panel.style.height = '0px';
    void panel.offsetHeight;
    requestAnimationFrame(() => {
      panel.style.transition = 'height 380ms cubic-bezier(0.22, 1, 0.36, 1)';
      panel.style.height = `${targetHeight}px`;
      const onEnd = () => {
        panel.style.height = 'auto';
        panel.removeEventListener('transitionend', onEnd);
      };
      panel.addEventListener('transitionend', onEnd);
    });
  }

  animateCollapse(panel) {
    const startHeight = panel.scrollHeight;
    panel.style.height = `${startHeight}px`;
    void panel.offsetHeight;
    requestAnimationFrame(() => {
      panel.style.transition = 'height 300ms cubic-bezier(0.4, 0, 0.2, 1)';
      panel.style.height = '0px';
    });
  }

  acceptAll() {
    this.state.preferences = { necessary: true, analytics: true, marketing: true, preferences: true };
    this.commit('accepted');
  }

  rejectAll() {
    this.state.preferences = { necessary: true, analytics: false, marketing: false, preferences: false };
    this.commit('rejected');
  }

  savePreferences() {
    this.commit('custom');
  }

  commit(consentType) {
    this.state.consented = consentType;
    const prefs = this.state.preferences;

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...prefs,
        timestamp: Date.now(),
        type: consentType
      }));
    } catch (_) {}

    this.applyConsent(prefs);
    this.dispatchEvent(new CustomEvent('marmitek:consent-changed', {
      bubbles: true,
      composed: true,
      detail: { ...prefs, type: consentType }
    }));

    this.showConfirmation(consentType);
  }

  applyConsent(prefs) {
    if (window.Shopify?.customerPrivacy?.setTrackingConsent) {
      window.Shopify.customerPrivacy.setTrackingConsent({
        analytics: prefs.analytics,
        marketing: prefs.marketing,
        preferences: prefs.preferences,
        sale_of_data: prefs.marketing
      }, () => {});
    }

    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        ad_storage: prefs.marketing ? 'granted' : 'denied',
        ad_user_data: prefs.marketing ? 'granted' : 'denied',
        ad_personalization: prefs.marketing ? 'granted' : 'denied',
        analytics_storage: prefs.analytics ? 'granted' : 'denied',
        functionality_storage: prefs.preferences ? 'granted' : 'denied',
        personalization_storage: prefs.preferences ? 'granted' : 'denied',
        security_storage: 'granted'
      });
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'marmitek_consent_update',
      consent_analytics: prefs.analytics,
      consent_marketing: prefs.marketing,
      consent_preferences: prefs.preferences
    });
  }

  showConfirmation(consentType) {
    const banner = this.shadowRoot.querySelector('.banner');
    const inner = this.shadowRoot.querySelector('.banner-inner');
    const message = consentType === 'accepted' ? this.l10n.confirm_accepted
      : consentType === 'rejected' ? this.l10n.confirm_rejected
      : this.l10n.confirm_saved;

    spring(0, 1, (v) => {
      inner.style.opacity = String(1 - v);
      inner.style.transform = `translateY(${v * 12}px)`;
    }, { stiffness: 300, damping: 30 });

    setTimeout(() => {
      banner.innerHTML = `
        <div class="confirmed">
          <span class="confirmed-icon">${CHECK_ICON}</span>
          <span>${message || ''}</span>
        </div>
      `;
      const confirmed = banner.querySelector('.confirmed');
      confirmed.style.opacity = '0';
      confirmed.style.transform = 'translateY(8px)';
      requestAnimationFrame(() => {
        confirmed.style.transition = 'opacity 280ms ease, transform 280ms cubic-bezier(0.22, 1, 0.36, 1)';
        confirmed.style.opacity = '1';
        confirmed.style.transform = 'translateY(0)';
      });

      setTimeout(() => this.dismiss(), 1600);
    }, 240);
  }

  dismiss() {
    const banner = this.shadowRoot.querySelector('.banner');
    spring(0, 120, (v) => {
      banner.style.transform = `translateY(${v}%)`;
      banner.style.opacity = String(Math.max(0, 1 - v / 60));
      if (v >= 119) this.remove();
    }, { stiffness: 220, damping: 26 });
  }
}

if (!customElements.get('cookie-banner')) {
  customElements.define('cookie-banner', CookieBanner);
}

export { CookieBanner };
